import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import LoginPage from './pages/login/LoginPage';
import POSPage from './pages/pos/POSPage';
import KDSPage from './pages/kds/KDSPage';
import InventoryPage from './pages/inventory/InventoryPage';
import LoyaltyPage from './pages/loyalty/LoyaltyPage';
import WastePage from './pages/waste/WastePage';
import PurchasingPage from './pages/purchasing/PurchasingPage';
import TransfersPage from './pages/transfers/TransfersPage';
import PromotionsPage from './pages/promotions/PromotionsPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import MenuManagementPage from './pages/menu-management/MenuManagementPage';
import IngredientsPage from './pages/ingredients/IngredientsPage';
import RecipeManagementPage from './pages/recipe-management/RecipeManagementPage';
import SettingsPage from './pages/settings/SettingsPage';
import OrdersPage from './pages/orders/OrdersPage';
import ProfilePage from './pages/profile/ProfilePage';
import ExecutiveDashboardPage from './pages/dashboard/ExecutiveDashboardPage';
import SalesReportsPage from './pages/reports/SalesReportsPage';
import PerformanceAnalyticsPage from './pages/reports/PerformanceAnalyticsPage';
import Layout from './components/layouts/Layout';
import KDSLayout from './components/layouts/KDSLayout';
import OfflineIndicator from './components/OfflineIndicator';
import { registerServiceWorker, requestPersistentStorage } from './services/offline/register-sw';
import { offlineDB } from './services/offline/db';

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  useEffect(() => {
    // Initialize offline infrastructure
    const initOffline = async () => {
      try {
        // Initialize IndexedDB
        await offlineDB.init();
        console.log('Offline DB initialized');

        // Register Service Worker
        await registerServiceWorker();
        console.log('Service Worker registered');

        // Request persistent storage
        const isPersisted = await requestPersistentStorage();
        if (isPersisted) {
          console.log('Persistent storage granted');
        } else {
          console.warn('Persistent storage not granted - data may be cleared');
        }
      } catch (error) {
        console.error('Failed to initialize offline infrastructure:', error);
      }
    };

    initOffline();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* POS - Standalone page with its own layout */}
        <Route
          path="/pos"
          element={
            <ProtectedRoute>
              <POSPage />
            </ProtectedRoute>
          }
        />

        {/* KDS - Standalone page with its own layout */}
        <Route
          path="/kds"
          element={
            <ProtectedRoute>
              <KDSLayout>
                <KDSPage />
              </KDSLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin/Back-office routes with shared Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<ExecutiveDashboardPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="loyalty" element={<LoyaltyPage />} />
          <Route path="loyalty-admin" element={<Navigate to="/loyalty" replace />} />
          <Route path="waste" element={<WastePage />} />
          <Route path="purchasing" element={<PurchasingPage />} />
          <Route path="transfers" element={<TransfersPage />} />
          <Route path="promotions" element={<PromotionsPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="reports" element={<SalesReportsPage />} />
          <Route path="performance-analytics" element={<PerformanceAnalyticsPage />} />
          <Route path="menu-management" element={<MenuManagementPage />} />
          <Route path="ingredients" element={<IngredientsPage />} />
          <Route path="recipe-management" element={<RecipeManagementPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>
      </Routes>

      {/* Offline status indicator */}
      <OfflineIndicator />
    </BrowserRouter>
  );
}

export default App;
